import { useEffect, useMemo } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { hasTokens } from '../lib/auth'
import { useMe } from '../hooks/useAuth'
import { useParticipations } from '../hooks/useParticipations'
import { useGoogleCalendarEvents } from '../hooks/useGoogleCalendarEvents'
import { CardSkeleton } from '../components/Skeleton'
import { EmptyState } from '../components/EmptyState'
import { HeroBar } from '../components/dashboard/HeroBar'
import { OperativoView } from '../components/dashboard/views/OperativoView'
import { DirezionaleView } from '../components/dashboard/views/DirezionaleView'
import { StrategicoView } from '../components/dashboard/views/StrategicoView'

export const Route = createFileRoute('/')({
  beforeLoad: () => {
    if (!hasTokens()) throw redirect({ to: '/login' })
  },
  component: HomePage,
})

// Format Date → YYYY-MM-DD using LOCAL components.
// Do NOT use toISOString().slice(0,10) — that converts to UTC and slips a day
// in non-UTC timezones (Italy UTC+1/+2 → midnight local = previous day UTC).
function ISO_DATE_FMT(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function dailyQuote(seed: string): string {
  const quotes = [
    'Ogni asta vinta inizia con un sopralluogo fatto bene.',
    'I numeri raccontano, le relazioni convertono.',
    'Il miglior lotto è quello che nessuno ha ancora visto.',
  ]
  let h = 0
  for (const c of seed) h = (h * 31 + c.charCodeAt(0)) >>> 0
  return quotes[h % quotes.length]
}

function HomePage() {
  const navigate = useNavigate()
  const { data: me, isLoading, isError } = useMe()

  const now = useMemo(() => new Date(), [])
  const today = useMemo(() => ISO_DATE_FMT(now), [now])

  // Contatori hero (leggeri).
  const { data: agendaToday } = useGoogleCalendarEvents(today, today)
  const { data: partData } = useParticipations({ page_size: 50, sort: 'data_asta' })

  useEffect(() => {
    if (isError && !hasTokens()) void navigate({ to: '/login' })
  }, [isError, navigate])

  if (isLoading) return <CardSkeleton />
  if (isError || !me) {
    return (
      <EmptyState
        icon="⚠️"
        title="Errore profilo"
        body="Impossibile caricare il profilo. Riprova il login."
        action={{ label: 'Torna al login', onClick: () => void navigate({ to: '/login' }) }}
      />
    )
  }

  const firstName = me.name.split(' ')[0]
  const todayFmt = now.toLocaleDateString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
  const impegniOggi = agendaToday?.items?.length ?? 0

  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000
  const asteProssime = (partData?.items ?? []).filter((p) => {
    if (!p.property_data_asta) return false
    const t = new Date(p.property_data_asta).getTime()
    return t >= now.getTime() && t <= now.getTime() + sevenDaysMs
  }).length

  const role = me.role
  const secondary = me.secondary_roles ?? []
  const isStrategico = role === 'supervisore'
  const isDirezionale = role === 'analista' || secondary.includes('responsabile')

  const anno = now.getFullYear()
  const mese = now.getMonth() + 1

  return (
    <>
      <HeroBar
        firstName={firstName}
        todayFmt={todayFmt}
        impegniOggi={impegniOggi}
        asteProssime={asteProssime}
        quote={dailyQuote(me.id ?? me.name)}
      />
      {isStrategico ? (
        <StrategicoView anno={anno} mese={mese} />
      ) : isDirezionale ? (
        <DirezionaleView />
      ) : (
        <OperativoView />
      )}
    </>
  )
}
